import React from 'react'
import { createPortal } from 'react-dom'
import type { ProjectLabel } from '@bandos/shared'
import { FONT_WEIGHT, TYPOGRAPHY } from '../../../../../styles/theme'
import {
  PreviewOverlay as PreviewOverlayStyled,
  PreviewCard,
  PreviewHeader,
  PreviewContent,
  PreviewLabel,
} from '../styles/PreviewStyles'

interface PreviewOverlayProps {
  mousePosition: { x: number; y: number }
  icon: React.ComponentType<{ size: number; color: string; strokeWidth?: number }>
  iconColor: string
  gradientColor: string
  borderColorOverride?: string
  categoryLabel: string
  categorySubheading?: string
  hideDefaultContent: boolean
  nodeText: string
  labels: ProjectLabel[] | undefined
  renderNodeContent?: (currentText: string) => React.ReactNode
  currentText: string
}

export const NodePreviewOverlay = React.memo(
  ({
    mousePosition,
    icon: CategoryIconComponent,
    iconColor,
    gradientColor,
    borderColorOverride,
    categoryLabel,
    categorySubheading,
    hideDefaultContent,
    nodeText,
    labels,
    renderNodeContent,
    currentText,
  }: PreviewOverlayProps) =>
    createPortal(
      <PreviewOverlayStyled $x={mousePosition.x} $y={mousePosition.y}>
        <PreviewCard $gradientColor={gradientColor} $borderColor={borderColorOverride}>
          {/* Full header with subheading */}
          <PreviewHeader>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CategoryIconComponent size={18} color={iconColor} />
                <span
                  style={{
                    fontSize: TYPOGRAPHY.body.fontSize,
                    fontWeight: FONT_WEIGHT.semibold,
                    color: iconColor,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {categoryLabel}
                </span>
              </div>
              {categorySubheading && (
                <div
                  style={{
                    fontSize: TYPOGRAPHY.caption.fontSize,
                    color: 'var(--color-text-secondary)',
                    marginTop: '4px',
                  }}
                >
                  {categorySubheading}
                </div>
              )}
            </div>
          </PreviewHeader>

          {/* Main content area */}
          {!hideDefaultContent && <PreviewContent>{nodeText || 'No content'}</PreviewContent>}

          {/* Custom node content (footers, lists, etc.) */}
          {renderNodeContent && renderNodeContent(currentText)}

          {/* Labels */}
          {labels && labels.length > 0 && (
            <div
              style={{
                padding: '8px 12px',
                display: 'flex',
                gap: '6px',
                flexWrap: 'wrap',
                borderTop: '1px solid var(--color-node-header-border)',
              }}
            >
              {labels.map((label) => (
                <PreviewLabel key={label.id} $color={label.color}>
                  {label.label_text}
                </PreviewLabel>
              ))}
            </div>
          )}
        </PreviewCard>
      </PreviewOverlayStyled>,
      document.body
    )
)
